import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, HandMetal } from 'lucide-react'
import { accountsApi } from '@/api'
import { Avatar } from '@/components/profile/Avatar'
import { formatPostTime } from '@/lib/dates'
import type { ReceivedClapEntry } from '@olimhub/shared'

function ClapRow({ entry, onNavigate }: { entry: ReceivedClapEntry; onNavigate: (postId: string) => void }) {
  const { account, postContent, postId, clappedAt } = entry
  return (
    <button
      onClick={() => onNavigate(postId)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        width: '100%',
        padding: '14px 16px',
        border: 'none',
        background: 'transparent',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
        textAlign: 'start',
      }}
    >
      <Avatar
        name={account.displayName}
        avatarUrl={account.avatarUrl}
        animalAvatar={account.animalAvatar}
        size="sm"
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-body)', lineHeight: 1.4 }}>
          <strong style={{ color: 'var(--text-strong)', fontWeight: 700 }}>@{account.username}</strong>
          {' '}
          <span style={{ color: 'var(--text-muted)' }}>👏</span>
        </p>
        <p style={{
          margin: '4px 0 0',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-xs)',
          color: 'var(--text-muted)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {postContent}
        </p>
        <span style={{ display: 'block', marginTop: 4, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
          {formatPostTime(clappedAt)}
        </span>
      </div>
    </button>
  )
}

export function ClapsReceivedPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ['claps-received'],
    queryFn: () => accountsApi.getMyClaps(),
  })

  const items = data?.items ?? []

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 16px',
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
      }}>
        <button
          type="button"
          aria-label={t('common.back')}
          onClick={() => navigate(-1)}
          style={{
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-muted)',
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, color: 'var(--text-strong)', fontSize: 'var(--fs-body)' }}>
          {t('claps.title')}
        </span>
      </div>

      {isLoading ? (
        Array.from({ length: 4 }).map((_, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--skeleton)' }} />
            <div style={{ flex: 1 }}>
              <div style={{ width: '40%', height: 12, borderRadius: 4, background: 'var(--skeleton)', marginBottom: 8 }} />
              <div style={{ width: '70%', height: 10, borderRadius: 4, background: 'var(--skeleton)' }} />
            </div>
          </div>
        ))
      ) : items.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 24px', textAlign: 'center' }}>
          <HandMetal size={48} strokeWidth={1.5} style={{ color: 'var(--text-faint)', marginBottom: 12 }} />
          <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
            {t('claps.empty')}
          </p>
        </div>
      ) : (
        items.map((entry, i) => (
          <ClapRow
            key={`${entry.accountId}-${entry.postId}-${i}`}
            entry={entry}
            onNavigate={(postId) => navigate(`/posts/${postId}`)}
          />
        ))
      )}
    </div>
  )
}
